import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon } from 'lucide-react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import GoogleDrive from '@/components/atoms/Icons/GoogleDrive';
import Intercom from '@/components/atoms/Icons/Intercom';
import Workvivo from '@/components/atoms/Icons/Workvivo';
import Zendesk from '@/components/atoms/Icons/Zendesk';
import {
  SidebarWrapper,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useDatasources from '@/hooks/useDatasources';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { selectIsGoogleDriveEnabled } from '@/redux/featureFlag/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { setDatasourceId } from '@/redux/session/actions';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { GoogleDrivePickedFile } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { hasDirtyFields, useHasDirtyFields } from '@/util/util';

import { useGoogleDriveSettings } from './googleDrive';
import { GoogleDriveForm } from './GoogleDriveForm';
import { IntercomForm } from './IntercomForm';
import { EXTERNAL_SOURCE_FIELD } from './layout';
import { useExternalSourceDatasourceId } from './useExternalSourceDatasourceId';
import { WorkvivoForm } from './WorkvivoForm';
import { ZendeskForm } from './ZendeskForm';
import { DATASOURCE_TYPE_TO_TAB } from '../../../constants';
import { ExternalSourceProvider, PROVIDER_BY_TYPE } from '../../../hooks/useExternalSourceIssue';
import { addExternalSourcesSchema } from '../../../validator';

type SourceType = ExternalSourceProvider | null;

// Field mapping for each source type
const SOURCE_FIELDS = {
  zendesk: ['subdomain', 'categories', 'language'],
  intercom: [], // No specific fields in this form
  workvivo: ['client_id', 'access_token', 'space_ids', 'pages', 'articles', 'goals'],
  gdrive: ['name', 'refresh_token', 'files'],
} as const;

interface FormValues {
  source: SourceType;
  subdomain?: string;
  categories?: string[];
  language?: string;
  keep_published?: boolean;
  client_id?: string;
  access_token?: string;
  space_ids?: string[];
  pages?: boolean;
  articles?: boolean;
  goals?: boolean;
  name?: string;
  refresh_token?: string;
  files?: GoogleDrivePickedFile[];
}

/**
 * Clean up form fields that don't belong to the selected source type
 */
function cleanupFormFields(
  form: ReturnType<typeof useForm<FormValues>>,
  selectedSource: SourceType
) {
  // Get all source types except the selected one
  const sourcesToCleanup = (Object.keys(SOURCE_FIELDS) as Array<keyof typeof SOURCE_FIELDS>).filter(
    (source) => source !== selectedSource
  );

  // Unregister fields from other source types
  sourcesToCleanup.forEach((source) => {
    SOURCE_FIELDS[source].forEach((fieldName) => {
      form.unregister(fieldName as keyof FormValues);
    });
  });
}

export const AddExternalSources = () => {
  // Redux selectors
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );

  // Hooks
  const { collectionId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const datasourceId = useExternalSourceDatasourceId();
  const { datasource, getStatus } = useDatasources(collectionId, datasourceId);

  const editingSource =
    !!datasourceId && getStatus === 'success' ? PROVIDER_BY_TYPE[datasource.type] : undefined;
  const isGoogleDriveEditing = editingSource === 'gdrive';
  const isAnyEditing = !!editingSource && editingSource !== 'intercom';

  const isGoogleDriveEnabled = useSelector(selectIsGoogleDriveEnabled);
  const { data: googleDriveSettings } = useGoogleDriveSettings();
  // An account that already connected a folder keeps editing it, flag or not.
  const showGoogleDrive =
    (isGoogleDriveEnabled && googleDriveSettings?.enabled) || isGoogleDriveEditing;

  const isEditingExistingSource =
    !!datasourceId &&
    getStatus === 'success' &&
    DATASOURCE_TYPE_TO_TAB[datasource.type] === 'external';

  // RHF
  const form = useForm<FormValues>({
    resolver: yupResolver(
      addExternalSourcesSchema(isAnyEditing, {
        collectionId,
        excludeName: isGoogleDriveEditing ? datasource.name : undefined,
      })
    ) as Resolver<FormValues>,
    defaultValues: {
      source: 'zendesk',
      subdomain: '',
      categories: [],
      language: '',
      client_id: '',
      access_token: '',
      space_ids: [],
      pages: false,
      articles: false,
      goals: false,
      ...(isAnyEditing && { keep_published: true }),
    },
  });

  // Local state
  const [selectedSource, setSelectedSource] = useState<SourceType>('zendesk');
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  // Handlers
  const handleClose = (withValidation: boolean = false) => {
    if (withValidation) {
      const hasUnsavedChanges = hasDirtyFields(form);

      if (hasUnsavedChanges) {
        setIsUnsavedChangesModalOpen(true);
        return;
      }
    }
    dispatch(setSidebarContent(null));
    dispatch(setDatasourceId(null));
  };

  useEffect(() => {
    if (isAnyEditing && editingSource) {
      setSelectedSource(editingSource);
      form.resetField('source', { defaultValue: editingSource });
    }
  }, [isAnyEditing, editingSource, datasource, form]);

  useEffect(() => {
    if (datasource) {
      form.resetField('source', { defaultValue: PROVIDER_BY_TYPE[datasource.type] ?? 'zendesk' });
    }
  }, [datasource, form]);

  const isDirty = useHasDirtyFields(form.control);
  useTrackFormState({ isDirty, formId: 'add-external-sources' });

  // Close the sidebar when the Escape key is pressed
  useEscapeKey(handleClose);

  return (
    <SidebarWrapper className="gap-4">
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">
          {isEditingExistingSource
            ? t('collections.add_external_sources.edit_title')
            : t('collections.add_external_sources.title')}
        </h2>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          type="button"
          onClick={() => handleClose(true)}
        >
          <XIcon size={16} className="text-muted-foreground" />
        </Button>
      </SidebarWrapperTitle>

      <FormProvider {...form}>
        <Form {...form}>
          <SidebarWrapperForm className="gap-4 px-6 py-0" onSubmit={(e) => e.preventDefault()}>
            <FormField
              control={form.control}
              name="source"
              render={({ field }) => (
                <FormItem className={EXTERNAL_SOURCE_FIELD}>
                  <FormLabel>{t('collections.select_source')}</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      setSelectedSource(value as SourceType);

                      // Clean up fields from other source types when switching
                      cleanupFormFields(form, value as SourceType);
                    }}
                    value={field.value || undefined}
                    disabled={isAnyEditing || !canWrite}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('common.select')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>{t('collections.tabs.external')}</SelectLabel>

                        <SelectItem value="zendesk">
                          <Zendesk /> Zendesk
                        </SelectItem>
                        <SelectItem value="intercom">
                          <Intercom /> Intercom
                        </SelectItem>
                        <SelectItem value="workvivo">
                          <Workvivo /> Workvivo
                        </SelectItem>
                        {showGoogleDrive && (
                          <SelectItem value="gdrive">
                            <GoogleDrive /> Google Drive
                          </SelectItem>
                        )}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            {selectedSource === 'intercom' && (
              <IntercomForm key={datasourceId} handleCancel={handleClose} />
            )}

            {selectedSource === 'zendesk' && (
              <ZendeskForm key={datasourceId} handleCancel={handleClose} />
            )}

            {selectedSource === 'workvivo' && (
              <WorkvivoForm key={datasourceId} handleCancel={handleClose} />
            )}

            {selectedSource === 'gdrive' && (
              <GoogleDriveForm key={datasourceId} handleCancel={handleClose} />
            )}
          </SidebarWrapperForm>
        </Form>
      </FormProvider>

      {isUnsavedChangesModalOpen && (
        <UnsavedChangesModal
          onDiscard={() => {
            dispatch(setSidebarContent(null));
            dispatch(setDatasourceId(null));
          }}
          onGoBack={() => {
            setIsUnsavedChangesModalOpen(false);
          }}
          onOpenChange={setIsUnsavedChangesModalOpen}
        />
      )}
    </SidebarWrapper>
  );
};
